/**
 * The `@` / `/` / `#` trigger logic, as pure functions.
 *
 * No React, no Quill, no DOM — the editor reads the caret and hands the text
 * before it to `detectTrigger`, so every rule below is unit-testable without
 * mounting anything. `@platform/shared-types` is imported TYPE-ONLY for the
 * same reason as `deltaOutline.ts`.
 *
 * THREE TRIGGERS, THREE VOCABULARIES:
 *   `@`  a merge field   — 51 catalog entries
 *   `/`  a block         — heading, list, table, callout, signature, break
 *   `#`  a cross-reference to a heading in this document
 *
 * One combined menu would be ~58 rows and an author would scroll past
 * furniture to reach a variable. Splitting them is the Notion/Slack
 * convention, so it needs no explaining.
 */
import type { MergeFieldDef } from '@platform/shared-types';
import type { OutlineHeading } from './deltaOutline';

export type TriggerKind = '@' | '/' | '#';

export interface TriggerMatch {
  kind: TriggerKind;
  /** The characters typed after the trigger, e.g. `sign` for `@sign`. */
  query: string;
  /**
   * Index of the trigger character itself, relative to the start of the text
   * that was passed in. The editor deletes from here through the caret before
   * inserting, so `@sign` never survives beside the chip it produced.
   */
  start: number;
}

/**
 * A query is letters, digits and underscores only. A SPACE CLOSES THE MENU.
 *
 * Without that bound, typing `@sign the agreement` would keep the menu open
 * over the whole sentence. Labels do contain spaces ("Signer name"), which is
 * why matching normalises both sides (see `normalise`) instead of widening
 * what counts as a query.
 */
const QUERY_CHARS = /^[a-zA-Z0-9_]*$/;

/** Beyond this the author is plainly writing prose, not picking a field. */
const MAX_QUERY = 32;

/**
 * Strip everything but letters and digits, and lowercase.
 *
 * This is what lets the space-free query `signername` match the label
 * `Signer name`, and `sign` match both `signer_name` and `Signer name`.
 */
function normalise(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * What the caret is currently triggering, or `null`.
 *
 * `textBefore` is the document text from the start of the CURRENT LINE up to
 * the caret. Passing the whole document would make the `/`-at-line-start rule
 * unexpressible.
 *
 * THE GUARDS, and what each one is for:
 *
 *  - `@` and `#` fire only at the start of a line or after WHITESPACE.
 *    Without this, `ops@rubytill.com` opens a field picker in the middle of
 *    every email address an author types, and `#` fires inside `issue#42`.
 *  - `/` fires ONLY at the start of a line, because a slash mid-sentence is
 *    nearly always a real slash — `and/or`, `24/7`, a URL path.
 */
export function detectTrigger(textBefore: string): TriggerMatch | null {
  for (let i = textBefore.length - 1; i >= 0; i -= 1) {
    const ch = textBefore[i];
    if (ch !== '@' && ch !== '/' && ch !== '#') continue;

    const query = textBefore.slice(i + 1);
    if (query.length > MAX_QUERY || !QUERY_CHARS.test(query)) return null;

    const prev = i === 0 ? '' : textBefore[i - 1];

    if (ch === '/') {
      // Line start only.
      if (i !== 0) return null;
      return { kind: '/', query, start: i };
    }

    if (i !== 0 && !/\s/.test(prev)) return null;
    return { kind: ch, query, start: i };
  }
  return null;
}

/* ── ranking ────────────────────────────────────────────────────────────── */

/**
 * Prefix beats substring, and the token name beats the label.
 *
 * So `@company` puts `company_legal_name` first rather than burying it under
 * something that merely mentions the word. Lower is better; `-1` means the
 * entry does not match at all.
 */
function rankOf(name: string, label: string, q: string): number {
  if (q === '') return 0;
  const n = normalise(name);
  const l = normalise(label);
  if (n.startsWith(q)) return 0;
  if (l.startsWith(q)) return 1;
  if (n.includes(q)) return 2;
  if (l.includes(q)) return 3;
  return -1;
}

/**
 * Catalog entries matching `query`, best first.
 *
 * Ties keep CATALOG ORDER — the sort is stable and the comparator only ever
 * compares ranks — so an empty query renders the catalog exactly as the
 * palette does, grouped and in the order someone curated.
 */
export function rankMergeFields(
  defs: readonly MergeFieldDef[],
  query: string,
): MergeFieldDef[] {
  const q = normalise(query);
  return defs
    .map((def, i) => ({ def, i, rank: rankOf(def.name, def.label, q) }))
    .filter((e) => e.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((e) => e.def);
}

/**
 * Headings matching `query`, best first.
 *
 * The section NUMBER is matched too, so `#3` finds section 3 — which is how
 * anyone who has the document in front of them actually refers to it.
 */
export function rankHeadings(
  headings: readonly OutlineHeading[],
  query: string,
): OutlineHeading[] {
  const q = normalise(query);
  return headings
    .map((h, i) => ({ h, i, rank: rankOf(h.number, h.text, q) }))
    .filter((e) => e.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((e) => e.h);
}

/* ── the `/` vocabulary ─────────────────────────────────────────────────── */

export type BlockInsertId =
  | 'heading1' | 'heading2' | 'heading3'
  | 'bullet' | 'ordered'
  | 'table' | 'callout' | 'pageBreak'
  /**
   * One per PARTY, not one generic "signature panel".
   *
   * The role is what the envelope addresses a signer by, so choosing it at
   * insert time is what makes the panel bind to a real person later. A single
   * generic panel left every document with `signerRole: 'signer'` — a role
   * that is not in `SIGNER_ROLES` at all, so nothing could ever be routed to
   * it.
   */
  | 'signCustomer' | 'signCompany' | 'signGuarantor' | 'signWitness';

/**
 * Insert id → the `signerRole` the panel carries. These are `SIGNER_ROLES`
 * values from shared-types; `platform` is us.
 */
export const SIGN_ROLE_FOR: Record<string, { role: string; caption: string }> = {
  signCustomer: { role: 'customer', caption: 'Customer' },
  signCompany: { role: 'platform', caption: 'Our company' },
  signGuarantor: { role: 'guarantor', caption: 'Personal guarantor' },
  signWitness: { role: 'witness', caption: 'Witness' },
};

export interface BlockInsertDef {
  id: BlockInsertId;
  label: string;
  hint?: string;
  /** Extra words the query may match, for things nobody calls by their label. */
  keywords?: string;
}

/**
 * What `/` offers. Ordered by how often a legal author reaches for it, not
 * alphabetically — heading and lists are most of the work.
 */
export const BLOCK_INSERTS: readonly BlockInsertDef[] = [
  { id: 'heading2', label: 'Heading', hint: 'Section', keywords: 'h2 section clause title' },
  { id: 'heading3', label: 'Sub-heading', hint: 'Sub-section', keywords: 'h3 subsection' },
  { id: 'heading1', label: 'Title', hint: 'Document title', keywords: 'h1' },
  { id: 'bullet', label: 'Bulleted list', keywords: 'ul unordered' },
  { id: 'ordered', label: 'Numbered list', keywords: 'ol numbered' },
  { id: 'table', label: 'Table', keywords: 'grid matrix' },
  { id: 'callout', label: 'Callout', hint: 'Info · warning · danger', keywords: 'note admonition highlight warning' },
  { id: 'signCustomer', label: 'Signature — Customer', hint: 'Signed by the customer', keywords: 'sign signature customer merchant client' },
  { id: 'signCompany', label: 'Signature — Our company', hint: 'Countersigned by us', keywords: 'sign signature company platform us countersign' },
  { id: 'signGuarantor', label: 'Signature — Guarantor', hint: 'Personal guarantee', keywords: 'sign signature guarantor guarantee personal' },
  { id: 'signWitness', label: 'Signature — Witness', keywords: 'sign signature witness' },
  { id: 'pageBreak', label: 'Page break', keywords: 'break page' },
];

export function rankBlockInserts(query: string): BlockInsertDef[] {
  const q = normalise(query);
  return BLOCK_INSERTS
    .map((def, i) => ({
      def,
      i,
      rank: rankOf(def.keywords ?? def.label, def.label, q),
    }))
    .filter((e) => e.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((e) => e.def);
}
